import { useNavigate } from 'react-router-dom'
import { useI18n } from '../../i18n'
import type { GrcSummaryRow } from '../../services/informationSystemComplianceApi'
import type {
  InformationSystemExecutiveSummary,
  RiskComplianceStandardRow,
} from '../../services/enterpriseOperationalUxApi'
import { getStandardDisplayName } from '../../utils/complianceStandard'
import { formatPercentOrMissing, formatRelativeTime, normalizeStability, stabilityColor } from '../../utils/operationalUx'
import LifecycleBadge from '../assets/LifecycleBadge'
import RiskComplianceOperationalIndicators from './RiskComplianceOperationalIndicators'
import styles from '../../styles/components/assets/AssetsRiskCompliance.module.scss'

type StandardProps = {
  kind: 'standard'
  row: RiskComplianceStandardRow
  selected?: boolean
  onSelect: () => void
}

type SystemProps = {
  kind: 'system'
  grc: GrcSummaryRow
  executive?: InformationSystemExecutiveSummary | null
  selected?: boolean
  onSelect: () => void
  onOpenPassport?: () => void
  onOpenGraph?: () => void
}

type Props = StandardProps | SystemProps

function workflowLabel(stage: string, t: (k: string, d: string) => string) {
  const map: Record<string, string> = {
    READINESS_ESTABLISHED: t('enterpriseOperationalUx.dataMap.workflowReady', 'Readiness established'),
    EVIDENCE_IN_PROGRESS: t('enterpriseOperationalUx.dataMap.workflowEvidence', 'Evidence in progress'),
    DEGRADED: t('enterpriseOperationalUx.dataMap.workflowDegraded', 'Degraded'),
    AUDIT_AT_RISK: t('enterpriseOperationalUx.dataMap.workflowAuditRisk', 'Audit at risk'),
    VERIFIED: t('enterpriseOperationalUx.dataMap.workflowVerified', 'Verified'),
  }
  return map[stage] || stage
}

export default function RiskComplianceExecutiveRow(props: Props) {
  const { t } = useI18n()
  const navigate = useNavigate()

  if (props.kind === 'standard') {
    const { row, selected, onSelect } = props
    const stability = normalizeStability(row.certificationRiskLevel)
    const name = getStandardDisplayName(t, row.standardCode)

    return (
      <article
        className={`${styles.rcExecutiveRow} ${selected ? styles.rcExecutiveRowSelected : ''} ${row.isDegradedStandard ? styles.rcExecutiveRowDegraded : ''}`.trim()}
        role="button"
        tabIndex={0}
        onClick={onSelect}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onSelect()
          }
        }}
      >
        <div className={styles.rcExecutiveRowInner}>
          <div className={styles.rcExecutiveIdentity}>
            <h4 className={styles.rcExecutiveTitle}>{name}</h4>
            <span className={styles.rcExecutiveKind}>{t('assets.grc.standardCode', 'Standard')}</span>
          </div>
          <RiskComplianceOperationalIndicators
            compact
            pressure={row.pressure}
            degraded={row.degradedControlCount > 0}
            staleEvidence={row.staleEvidenceCount > 0}
            runtimeWarning={Boolean(row.latestRuntimeEvent)}
            unstableWorkflow={Boolean(row.workflowStage)}
            certificationRisk={row.certificationRiskLevel}
          />
          <div className={styles.rcExecutiveCols}>
            <div className={styles.rcExecutiveCol}>
              <p className={styles.rcColLabel}>{t('standardGovernance.selfAssessment', 'Self-assessment')}</p>
              <p className={styles.rcColValue}>
                {row.selfPercent != null ? formatPercentOrMissing(row.selfPercent, '') : t('standardGovernance.notSelfAssessed', 'Not self-assessed')}
              </p>
            </div>
            <div className={styles.rcExecutiveCol}>
              <p className={styles.rcColLabel}>{t('standardGovernance.verified', 'Verified')}</p>
              <p className={styles.rcColValue}>
                {row.verifiedPercent != null ? formatPercentOrMissing(row.verifiedPercent, '') : t('standardGovernance.notVerified', 'Not verified')}
              </p>
            </div>
            <div className={styles.rcExecutiveCol}>
              <p className={styles.rcColLabel}>{t('enterpriseOperationalUx.certificationStability', 'Certification stability')}</p>
              <p className={styles.rcColValue} style={{ color: stabilityColor(stability) }}>
                {row.certificationRiskLevel || '—'}
              </p>
            </div>
            <div className={styles.rcExecutiveCol}>
              <p className={styles.rcColLabel}>{t('enterpriseOperationalUx.readinessTrajectory', 'Readiness trajectory')}</p>
              <p className={styles.rcColValue}>{row.readinessTrajectory || '—'}</p>
            </div>
          </div>
          {row.storytelling ? <p className={styles.rcStoryLine}>{row.storytelling}</p> : null}
          {row.latestRuntimeEvent ? (
            <p className={styles.rcRuntimeLine}>
              <span className={styles.rcRuntimeDot} aria-hidden />
              {row.latestRuntimeEvent.eventType} — {formatRelativeTime(row.latestRuntimeEvent.at)}
            </p>
          ) : null}
          {row.workflowStage ? (
            <button
              type="button"
              className={styles.rcWorkflowChip}
              onClick={(e) => {
                e.stopPropagation()
                navigate(`/compliance-workflow/${encodeURIComponent(row.standardCode)}`)
              }}
            >
              {workflowLabel(row.workflowStage, t)}
            </button>
          ) : null}
        </div>
      </article>
    )
  }

  const { grc, executive, selected, onSelect, onOpenPassport, onOpenGraph } = props
  const notAvailable = t('enterpriseOperationalUx.assessmentNotAvailable', 'Self/verified: Not available at system level')

  return (
    <article
      className={`${styles.rcExecutiveRow} ${selected ? styles.rcExecutiveRowSelected : ''} ${(executive?.degradedControlCount || 0) > 0 ? styles.rcExecutiveRowDegraded : ''}`.trim()}
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSelect()
        }
      }}
    >
      <div className={styles.rcExecutiveRowInner}>
        <div className={styles.rcExecutiveIdentity}>
          <h4 className={styles.rcExecutiveTitle}>{grc.name}</h4>
          <LifecycleBadge status={grc.lifecycleStatus} />
        </div>
        {executive?.hasEnoughHistory ? (
          <RiskComplianceOperationalIndicators
            compact
            pressure={executive.pressure}
            degraded={(executive.degradedControlCount || 0) > 0}
            staleEvidence={(executive.staleEvidenceCount || 0) > 0}
            runtimeWarning={Boolean(executive.latestRuntimeEvent)}
            unstableWorkflow={(executive.workflowStages || []).some((w) => w.workflowStage === 'DEGRADED' || w.workflowStage === 'AUDIT_AT_RISK')}
            certificationRisk={executive.certificationStability}
          />
        ) : null}
        <div className={styles.rcExecutiveCols}>
          <div className={styles.rcExecutiveCol}>
            <p className={styles.rcColLabel}>{t('standardGovernance.selfAssessment', 'Self-assessment')}</p>
            <p className={styles.rcColValueMuted}>{notAvailable}</p>
          </div>
          <div className={styles.rcExecutiveCol}>
            <p className={styles.rcColLabel}>{t('standardGovernance.verified', 'Verified')}</p>
            <p className={styles.rcColValueMuted}>{notAvailable}</p>
          </div>
          <div className={styles.rcExecutiveCol}>
            <p className={styles.rcColLabel}>{t('enterpriseOperationalUx.systemPressure', 'System pressure')}</p>
            <p className={styles.rcColValue}>{executive?.pressure || '—'}</p>
          </div>
          <div className={styles.rcExecutiveCol}>
            <p className={styles.rcColLabel}>{t('assets.grc.controlCoverage', 'Coverage')}</p>
            <p className={styles.rcColValue}>
              {grc.controlCoveragePercent != null ? `${grc.controlCoveragePercent}%` : t('assets.riskCompliance.notAvailable', 'N/A')}
            </p>
          </div>
        </div>
        {executive?.explanations?.length ? <p className={styles.rcStoryLine}>{executive.explanations.join(' · ')}</p> : null}
        <div className={styles.rcRowActions} onClick={(e) => e.stopPropagation()}>
          {onOpenPassport ? (
            <button type="button" className={styles.rowActionBtn} onClick={onOpenPassport}>
              {t('assets.grc.openPassport', 'Passport')}
            </button>
          ) : null}
          {onOpenGraph ? (
            <button type="button" className={styles.rowActionBtn} onClick={onOpenGraph}>
              {t('assets.graph.openGraph', 'Graph')}
            </button>
          ) : null}
        </div>
      </div>
    </article>
  )
}
